import { useState, useContext } from "react";
import axios from 'axios';

import { AppContext } from '../../context.js';

import { MoreVertical, Comment, ChevronDown, ChevronUp, ArrowRight, Plus, CheckLarge, Minus, Close } from "../../files/icons/index.js";

import { Status } from './component.js';
import Editor from "./editor.js";
import Drop from "./drop.js";
import Chat from "./chat.js";
import PhysicianPicker from "./physicianPicker.js";
import { FullName } from '../../utils/string.js';

import { DateToFormat, DatetimeToDate } from "../../utils/datetime.js";

function PlanSectionCard(props) {
  const context = useContext(AppContext);

  const { section: initialSection, planID, planTemplate, users, onNoteAdded } = props;

  const [section, setSection] = useState(initialSection);

  const [closeSectionStatusDrop, setCloseSectionStatusDrop] = useState(0);

  const [hoveredSectionAction, setHoveredSectionAction] = useState(null);

  const [changeSectionStatusModalVisible, setChangeSectionStatusModalVisible] = useState(false);
  const [changeSectionStatusModalData, setChangeSectionStatusModalData] = useState(null);

  const [expandedChat, setExpandedChat] = useState(() => initialSection.hasNotes === true);

  // Find section template by matching template ID
  function getSectionTemplate() {
    if (!planTemplate || !planTemplate.sections) return null;
    return planTemplate.sections.find(t => t.id === section.templateID);
  }

  // Change section title (local state)
  function changeSectionTitle(text) {
    setSection(prev => ({ ...prev, title: text }));
  }

  function keyUpSectionTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  // Update section title (API)
  function updateSectionTitle() {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/updateTitle', {
      id: planID,
      sectionID: section.id,
      title: section.title
    }).then((response) => {
      // Title already updated in local state
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Update section description (API)
  function updateSectionDescription(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/updateDescription', {
      id: planID,
      sectionID: section.id,
      description: text
    }).then((response) => {
      setSection(prev => ({ ...prev, description: text }));
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Change action title (local state)
  function changeSectionActionTitle(actionID, text) {
    setSection(prev => ({
      ...prev,
      actions: prev.actions.map(action => {
        if (action.id === actionID) {
          return { ...action, title: text };
        }
        return action;
      })
    }));
  }

  function keyUpSectionActionTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  // Update section action (API)
  function updateSectionAction(actionID, title, status) {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/updateAction', {
      id: planID,
      sectionID: section.id,
      actionID: actionID,
      title: title,
      status: status,
    }).then((response) => {
      setSection(prev => {
        let updated = {
          ...prev,
          actions: prev.actions.map(action => {
            if (action.id === actionID) {
              return { ...action, title: title, status: status };
            }
            return action;
          }),
          ...(response.data.statusType && {
            statusType: response.data.statusType,
            statusID: response.data.statusID,
            statusName: response.data.statusName,
            statusReasonID: response.data.statusReasonID,
            statusReasonName: response.data.statusReasonName,
          }),
          ...(response.data.closedAt !== undefined && {
            closedAt: response.data.closedAt,
            closedBy: response.data.closedBy,
            closedByName: response.data.closedByName,
          }),
        };
        return updated;
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Create section action (API)
  function createSectionAction() {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/createAction', {
      id: planID,
      sectionID: section.id,
    }).then((response) => {
      setSection(prev => ({
        ...prev,
        actions: [...prev.actions, response.data],
      }));
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Delete section action (API)
  function deleteSectionAction(actionID) {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/deleteAction', {
      id: planID,
      sectionID: section.id,
      actionID: actionID,
    }).then((response) => {
      setSection(prev => ({
        ...prev,
        actions: prev.actions.filter(action => action.id !== actionID),
      }));
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Update actions autocomplete toggle (API)
  function updateSectionActionsAutocomplete() {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/updateActionsAutocomplete', {
      id: planID,
      sectionID: section.id,
    }).then((response) => {
      setSection(prev => {
        let updated = {
          ...prev,
          actionsAutocomplete: !prev.actionsAutocomplete,
          ...(response.data.statusType && {
            statusType: response.data.statusType,
            statusID: response.data.statusID,
            statusName: response.data.statusName,
            statusReasonID: response.data.statusReasonID,
            statusReasonName: response.data.statusReasonName,
          }),
          ...(response.data.closedAt !== undefined && {
            closedAt: response.data.closedAt,
            closedBy: response.data.closedBy,
            closedByName: response.data.closedByName,
          }),
        };

        // If autocomplete caused completion, mark all actions as completed
        if (response.data.statusType === "COMPLETED") {
          updated.actions = prev.actions.map(action => ({
            ...action,
            status: true
          }));
        }

        return updated;
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Update section status (API)
  function updateSectionStatus(statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/plans/sections/updateStatus', {
      id: planID,
      sectionID: section.id,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      setSection(prev => {
        let updated = {
          ...prev,
          statusType: response.data.statusType,
          statusID: response.data.statusID,
          statusName: response.data.statusName,
          statusReasonID: response.data.statusReasonID,
          statusReasonName: response.data.statusReasonName,
          closedAt: response.data.closedAt,
          closedBy: response.data.closedBy,
          closedByName: response.data.closedByName,
        };

        // If section is completed, mark all actions as completed
        if (response.data.statusType === "COMPLETED") {
          updated.actions = prev.actions.map(action => ({
            ...action,
            status: true
          }));
        }

        return updated;
      });
      setCloseSectionStatusDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Change section status - show modal if reasons exist
  function changeSectionStatus(status) {
    if (status.reasons && status.reasons.length > 0) {
      setChangeSectionStatusModalData({ status: status, reason: null });
      setChangeSectionStatusModalVisible(true);
    } else {
      updateSectionStatus(status.id, null);
    }
  }

  function changeSectionStatusConfirm(statusID, reasonID) {
    setChangeSectionStatusModalVisible(false);
    setChangeSectionStatusModalData(null);
    updateSectionStatus(statusID, reasonID);
  }

  // Render section status
  function renderSectionStatus() {
    const sectionTemplate = getSectionTemplate();
    if (!sectionTemplate) return <></>;

    const status = sectionTemplate.statuses.find(s => s.id === section.statusID);
    if (!status) return <></>;

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  // Render next statuses dropdown
  function renderSectionNextStatuses() {
    const sectionTemplate = getSectionTemplate();
    if (!sectionTemplate) return <></>;

    const currentStatus = sectionTemplate.statuses.find(s => s.id === section.statusID);
    if (!currentStatus) return <></>;

    if (!currentStatus.next || currentStatus.next.length === 0) {
      return (
        <div className="typo-medium-150">
          Section has no next statuses
        </div>
      );
    }

    return sectionTemplate.statuses.filter(status => currentStatus.next.includes(status.id)).map((status, idx) => {
      return (
        <div key={"next_status_" + idx} className="side-item small typo-medium-150" onClick={() => changeSectionStatus(status)}>
          To <ArrowRight height={14} width={16} color={'grey'} /> <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
        </div>
      );
    });
  }

  // Toggle chat expansion
  function toggleChat() {
    setExpandedChat(prev => !prev);
  }

  // Render section card
  function renderSection() {
    return (
      <div className="mb-2">
        <div className="card">
          <div className="card-header sm">
            <div className="d-flex justify-content-between align-items-center">
              <input
                type="text"
                className="input borderless typo-strong-200 w-100"
                placeholder="Title..."
                value={section.title}
                onChange={(e) => changeSectionTitle(e.target.value)}
                onBlur={() => updateSectionTitle()}
                onKeyUp={(e) => keyUpSectionTitle(e)}
              />
              <div className="d-flex justify-content-end align-items-center w-25">
                {renderSectionStatus()}
                <Drop close={closeSectionStatusDrop} side={"right"} className="ms-1">
                  <div className="item rect">
                    <MoreVertical height={14} width={16} color={'grey'} />
                  </div>
                  <div className="card" style={{width: 200}}>
                    <div className="card-header sm">
                      <div className="typo-normal-150 text-muted">Change status:</div>
                      {renderSectionNextStatuses()}
                    </div>
                  </div>
                </Drop>
              </div>
            </div>

            <Editor
              placeholder="Section description..."
              value={section.description}
              onChangeID={section.id}
              onBlur={updateSectionDescription}
              toolbar="limited"
              className="text-editor borderless"
            />

            <div className="mt-1">
              {section.actions.map((action) => {
                return (
                  <div
                    key={"section_" + section.id + "_action_" + action.id}
                    className="item d-flex justify-content-start align-items-center"
                    onMouseEnter={() => setHoveredSectionAction(action.id)}
                    onMouseLeave={() => setHoveredSectionAction(null)}
                  >
                    <div className="d-flex justify-content-start align-items-center" style={{cursor: 'pointer'}} onClick={() => updateSectionAction(action.id, action.title, !action.status)}>
                      {action.status ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
                    </div>
                    <input
                      type="text"
                      className="input borderless typo-normal-150 w-100"
                      style={{backgroundColor: "transparent"}}
                      placeholder="Action title..."
                      value={action.title}
                      onChange={(e) => changeSectionActionTitle(action.id, e.target.value)}
                      onBlur={() => updateSectionAction(action.id, action.title, action.status)}
                      onKeyUp={(e) => keyUpSectionActionTitle(e)}
                    />
                    {hoveredSectionAction === action.id && (
                      <div className="d-flex justify-content-start align-items-center" style={{cursor: 'pointer'}} onClick={() => deleteSectionAction(action.id)}>
                        <Close height={16} width={16} color={'firebrick'} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="d-flex justify-content-start align-items-center mt-2">
                <div className="item" onClick={() => createSectionAction()}>
                  <Plus height={16} width={16} color={'lightgrey'} />
                  <div className="typo-normal-125 text-muted">Add action</div>
                </div>

                {section.actions.length > 0 && (
                  <div className="item ms-2" onClick={() => updateSectionActionsAutocomplete()}>
                    {section.actionsAutocomplete ? <CheckLarge height={16} width={16} color={'darkseagreen'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
                    <div className="typo-normal-125 text-muted">Autocomplete turned {section.actionsAutocomplete ? "on" : "off"}</div>
                  </div>
                )}
              </div>
            </div>

            {section.closedAt && (
              <div className="d-flex justify-content-start align-items-center mt-2">
                <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(section.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(section.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-2">
              <div className="d-flex justify-content-start align-items-center w-100">
                
              </div>
              <div className="d-flex justify-content-end align-items-bottom">
                <div onClick={() => toggleChat()}>
                  {expandedChat ? (
                    <div className="item grey">
                      <Comment height={16} width={16} color={'grey'} />
                      <span className="typo-normal-150">Notes</span>
                      <ChevronUp height={14} width={16} color={'grey'} />
                    </div>
                  ) : (
                    <div className="item grey">
                      <Comment height={16} width={16} color={'grey'} />
                      <span className="typo-normal-150">Notes</span>
                      <ChevronDown height={14} width={16} color={'grey'} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {expandedChat && (
            <div className="card-content sm">
              <Chat
                planID={planID}
                sectionID={section.id}
                createMessageCallback={() => {
                  setExpandedChat(true);
                  if (onNoteAdded) onNoteAdded();
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {changeSectionStatusModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeSectionStatusModalVisible(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="card">
                <div className="card-header">
                  <span className="typo-header-3xLarge">Confirm section status change</span><br/>
                  <span className="typo-normal-200 text-muted">Please choose a reason to change this section status</span>
                </div>
                <div className="card-content">
                  {changeSectionStatusModalData.status.reasons.map((reason, ri) => {
                    return (
                      <div key={"reason_" + ri} className="side-item" onClick={() => changeSectionStatusConfirm(changeSectionStatusModalData.status.id, reason.id)}>
                        <div className="typo-strong-200">{reason.title}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {renderSection()}
    </>
  );
}

export default PlanSectionCard;
